import {
  GoogleAuthProvider,
  getRedirectResult,
  onAuthStateChanged,
  signInWithRedirect,
  signOut,
} from "firebase/auth";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "@/components/toast";
import { Button } from "@/components/ui/button";
import { auth } from "@/lib/infrastructure/auth";
import logger from "@/lib/logger";

export default function LoginPage() {
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const setServerSession = useCallback(
    async (idToken: string): Promise<boolean> => {
      try {
        const response = await fetch("/api/auth/login", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idToken }),
        });
        if (!response.ok) {
          throw new Error(await response.text());
        }
        return true;
      } catch (error) {
        logger.error({ err: error }, "[Login] Failed to set server session");
        toast({
          type: "error",
          description: "Failed to synchronize session with the server.",
        });
        return false;
      }
    },
    []
  );

  useEffect(() => {
    let mounted = true;
    let unsubscribe: (() => void) | undefined;

    // When auth is disabled, redirect immediately without touching Firebase.
    const checkAuthDisabled = async (): Promise<boolean> => {
      if (window.__VERDEREM_CONFIG__) {
        return window.__VERDEREM_CONFIG__.authDisabled ?? false;
      }
      try {
        const res = await fetch("/api/config");
        const cfg = (await res.json()) as { authDisabled?: boolean };
        return cfg.authDisabled ?? false;
      } catch {
        return false;
      }
    };

    const initAuth = async () => {
      logger.debug("[Login] Initializing auth check...");

      const authDisabled = await checkAuthDisabled();
      if (!mounted) {
        return;
      }
      if (authDisabled) {
        logger.info("[Login] Auth disabled — bypassing login, redirecting");
        navigate("/launch");
        return;
      }

      try {
        const result = await getRedirectResult(auth);
        logger.debug(
          { hasUser: !!result?.user },
          "[Login] getRedirectResult outcome"
        );

        if (!mounted) {
          return;
        }

        if (result?.user) {
          logger.info({ uid: result.user.uid }, "[Login] Redirect successful");
          const idToken = await result.user.getIdToken();
          const success = await setServerSession(idToken);
          if (mounted && success) {
            navigate("/launch");
          } else {
            setIsLoading(false);
          }
          return;
        }
      } catch (error) {
        if (mounted) {
          logger.error({ err: error }, "[Login] Redirect error");
          const errorMessage =
            error instanceof Error ? error.message : String(error);
          toast({
            type: "error",
            description: `Authentication failed: ${errorMessage}. Please try again.`,
          });
          setIsLoading(false);
        }
      }

      // After an explicit logout (signOut redirects here with ?signedout=1), do
      // NOT auto-re-authenticate. Firebase can restore a persisted session on
      // load, and the onAuthStateChanged auto-login below would immediately mint
      // a new server session for it — logging the SAME account straight back in
      // and making it impossible to switch accounts. Show the sign-in button
      // instead; a deliberate sign-in still completes via getRedirectResult
      // above. (A genuine, non-logout visit has no flag and auto-logins as before.)
      const justSignedOut =
        new URLSearchParams(window.location.search).get("signedout") === "1";
      if (justSignedOut) {
        if (mounted) {
          setIsLoading(false);
        }
        // Defence-in-depth: signOut()'s immediate redirect can race the Firebase
        // IndexedDB persistence write, so a session may survive and resurface on
        // a later (flagless) visit — auto-logging the same account back in. Clear
        // it again here, where there's no pending navigation to abort the write.
        signOut(auth).catch((error) => {
          logger.error(
            { err: error },
            "[Login] Failed to clear Firebase session"
          );
        });
        return;
      }

      if (mounted) {
        unsubscribe = onAuthStateChanged(auth, async (user) => {
          if (!mounted) {
            return;
          }
          if (user) {
            setIsLoading(true);
            const idToken = await user.getIdToken();
            const success = await setServerSession(idToken);
            if (mounted && success) {
              navigate("/launch");
            } else {
              setIsLoading(false);
            }
          } else {
            setIsLoading(false);
          }
        });
      }
    };

    initAuth();
    return () => {
      mounted = false;
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [navigate, setServerSession]);

  const handleLogin = async () => {
    setIsLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: "select_account" });
      await signInWithRedirect(auth, provider);
    } catch (error) {
      logger.error({ err: error }, "[Login] Sign-in with redirect failed");
      toast({
        type: "error",
        description: "An unexpected error occurred during login.",
      });
      setIsLoading(false);
    }
  };

  return (
    <div className="flex h-dvh w-screen items-start justify-center bg-background pt-12 md:items-center md:pt-0">
      <div className="flex w-full max-w-md flex-col gap-12 overflow-hidden rounded-2xl border border-zinc-200 p-8 dark:border-zinc-800">
        <div className="flex flex-col items-center justify-center gap-2 text-center">
          <h3 className="font-semibold text-xl">Sign In</h3>
          <p className="text-muted-foreground text-sm">
            Sign in to your account to continue
          </p>
        </div>
        <Button className="w-full" disabled={isLoading} onClick={handleLogin}>
          {isLoading ? "Please wait..." : "Sign in with Google"}
        </Button>
        {process.env.NEXT_PUBLIC_DEBUG_MODE === "true" && (
          <Button
            className="w-full"
            disabled={isLoading}
            onClick={async () => {
              setIsLoading(true);
              try {
                const { signInWithCustomToken } = await import("firebase/auth");
                const res = await fetch("/api/auth/dev-token");
                if (!res.ok) {
                  throw new Error("Failed to fetch dev token");
                }
                const { customToken } = (await res.json()) as {
                  customToken: string;
                };
                const userCred = await signInWithCustomToken(auth, customToken);
                const idToken = await userCred.user.getIdToken();
                const success = await setServerSession(idToken);
                if (success) {
                  navigate("/launch");
                } else {
                  setIsLoading(false);
                }
              } catch {
                toast({
                  type: "error",
                  description: "Dev sign-in failed.",
                });
                setIsLoading(false);
              }
            }}
            variant="outline"
          >
            Dev Sign In
          </Button>
        )}
      </div>
    </div>
  );
}
